import React, { useEffect, useMemo, useRef } from 'react';

import useRefSetter from 'hooks/use-ref-setter';
import { Navigation } from 'pages/Bulletin/constants';

import AffectedIcon from './assets/affected.svg';
import CodeIcon from './assets/code.svg';
import DetectionIcon from './assets/detection.svg';
import FixIcon from './assets/fix.svg';
import GlobeIcon from './assets/globe.svg';
import PackagesIcon from './assets/packages.svg';
import RefsIcon from './assets/refs.svg';
import RelatedIcon from './assets/related.svg';
import SocialIcon from './assets/social.svg';
import NavBarItem from './components/NavBarItem';
import useStyles from './styles';

interface Props {
    value: Navigation | undefined;
    values: Navigation[];
    collapsed?: boolean;
    onChange: (value: Navigation) => void;
    className?: string;
}

const NavBar: React.ForwardRefRenderFunction<HTMLDivElement, Props> = (
    { value, values, onChange, collapsed, className },
    ref
) => {
    const { classes, cx } = useStyles();
    const containerRef = useRef<HTMLElement | null>(null);
    const setRef = useRefSetter(containerRef, ref);

    const options = useMemo(() => {
        return [
            { value: Navigation.RELATED, title: 'Related', icon: RelatedIcon },
            { value: Navigation.DETECTIONS, title: 'Detection', icon: DetectionIcon },
            { value: Navigation.AFFECTED, title: 'Affected', icon: AffectedIcon },
            { value: Navigation.PACKAGES, title: 'Packages', icon: PackagesIcon },
            { value: Navigation.REFERENCES, title: 'Refs', icon: RefsIcon },
            { value: Navigation.PATHS, title: 'Paths', icon: GlobeIcon },
            { value: Navigation.SOCIAL, title: 'Social', icon: SocialIcon },
            { value: Navigation.CODE, title: 'Code', icon: CodeIcon },
            { value: Navigation.FIX, title: 'Fix', icon: FixIcon }
        ].filter(({ value }) => values.includes(value));
    }, [values]);

    useEffect(() => {
        if (containerRef.current) {
            const containerBbox = containerRef.current.getBoundingClientRect();
            const item = containerRef.current.querySelector(`[data-value="${value}"]`);
            if (item instanceof HTMLElement) {
                const itemBbox = item.getBoundingClientRect();

                if (itemBbox.right > containerBbox.right) {
                    return containerRef.current.scrollBy({
                        behavior: 'smooth',
                        left: itemBbox.right - containerBbox.right
                    });
                }

                if (itemBbox.left < containerBbox.left) {
                    return containerRef.current.scrollBy({
                        behavior: 'smooth',
                        left: itemBbox.left - containerBbox.left
                    });
                }
            }
        }
    }, [value]);

    return (
        <div className={cx(classes.container, className)} ref={setRef}>
            {options.map((option) => (
                <NavBarItem
                    key={option.value}
                    collapsed={collapsed}
                    icon={option.icon}
                    title={option.title}
                    active={option.value === value}
                    onClick={() => onChange(option.value)}
                    data-value={option.value}
                />
            ))}
        </div>
    );
};

export default React.forwardRef(NavBar);
